import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { LogIn, Phone, Mail, MapPin, Clock, ArrowRight, SprayCan, Shield, Sparkles, Wrench, Layers, Scissors, Images, Star, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/brand";
import { normalizePhone } from "@/lib/share";
import { BACKEND_URL } from "@/lib/backendUrl";

// Only http(s) links may be rendered from editable site content (blocks javascript:/data: URLs)
const safeUrl = (u) => (/^https?:\/\//i.test(String(u || "").trim()) ? String(u).trim() : "#");

const API = `${BACKEND_URL}/api`;
const ICONS = [SprayCan, Layers, Wrench, Sparkles, Shield, Scissors];
const NAV = [["about", "About"], ["services", "Services"], ["pricing", "Pricing"], ["gallery", "Our Work"], ["reviews", "Reviews"], ["contact", "Contact"]];

const fmt = (sym, n) => (Number(n) > 0 ? `from ${sym}${Number(n).toLocaleString()}` : "Ask us");

function TopBar({ shop }) {
  const nav = NAV;
  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0B0F17]/85 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-3 min-w-0">
          <img src={BRAND.emblem} alt="" className="w-10 h-10 rounded-xl bg-white object-contain p-0.5" />
          <div className="min-w-0 hidden sm:block">
            <div className="font-heading font-black leading-none truncate">{shop?.name || BRAND.fullName}</div>
            <div className="text-[11px] text-sky-300/80 tracking-widest uppercase">{BRAND.tagline}</div>
          </div>
        </a>
        <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-slate-300">
          {nav.map(([id, label]) => <a key={id} href={`#${id}`} className="hover:text-amber-300 transition-colors" data-testid={`site-nav-${id}`}>{label}</a>)}
        </nav>
        <Button asChild data-testid="site-login-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold shrink-0">
          <Link to="/login"><LogIn className="w-4 h-4 mr-2" /> Login</Link>
        </Button>
      </div>
    </header>
  );
}

function Hero({ site, shop }) {
  const wa = shop?.phone ? `https://wa.me/${normalizePhone(shop.phone, shop.country_code)}` : null;
  return (
    <section id="top" className="relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(56,189,248,0.18),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(245,158,11,0.18),transparent_50%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-amber-400 font-semibold">Since 1988 · Singapore</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-4 leading-[1.05]" data-testid="site-hero-title">{site.hero_title}</h1>
          <p className="text-base md:text-lg text-slate-300 mt-6 max-w-xl">{site.hero_subtitle}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button asChild className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold h-12 px-6"><a href="#pricing" data-testid="site-hero-pricing">See pricing <ArrowRight className="w-4 h-4 ml-2" /></a></Button>
            {wa && <Button asChild className="bg-[#25D366] hover:bg-[#1ebe5b] text-slate-950 font-bold h-12 px-6"><a href={wa} target="_blank" rel="noreferrer" data-testid="site-hero-whatsapp">WhatsApp us</a></Button>}
            {!wa && <Button asChild variant="outline" className="border-slate-600 text-slate-200 h-12 px-6"><a href="#contact">Contact us</a></Button>}
          </div>
        </div>
        <div className="relative">
          <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-sky-500/20 to-amber-500/20 blur-2xl" />
          <img src={BRAND.logo} alt={shop?.name} className="relative w-full max-w-sm mx-auto rounded-[2rem] shadow-2xl shadow-black/50 ring-1 ring-white/10" />
        </div>
      </div>
    </section>
  );
}

const Section = ({ id, kicker, title, children }) => (
  <section id={id} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 scroll-mt-20">
    <p className="text-xs uppercase tracking-[0.3em] text-amber-400 font-semibold">{kicker}</p>
    <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2 mb-10">{title}</h2>
    {children}
  </section>
);

function Pricing({ site, sym }) {
  const rows = (site.public_pricing || []).filter((r) => r.label);
  return (
    <Section id="pricing" kicker="General Pricing" title="Full car respray, priced by vehicle size">
      <div className="rounded-2xl border border-slate-800 bg-[#151D2E] overflow-hidden" data-testid="site-pricing-table">
        <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] text-[11px] sm:text-xs uppercase tracking-wider text-slate-950 font-bold bg-amber-400">
          <div className="px-4 py-3">Service</div><div className="px-2 sm:px-4 py-3 text-center">Small<span className="hidden sm:inline"> · Hatch</span></div><div className="px-2 sm:px-4 py-3 text-center">Medium<span className="hidden sm:inline"> · Sedan</span></div><div className="px-2 sm:px-4 py-3 text-center">Large<span className="hidden sm:inline"> · SUV/MPV</span></div>
        </div>
        {rows.map((r, i) => (
          <div key={r._id || `${r.label}-${i}`} className="grid grid-cols-[1.4fr_1fr_1fr_1fr] items-center border-t border-slate-800 text-sm">
            <div className="px-4 py-4 font-semibold">{r.label}</div>
            {["small", "medium", "large"].map((k) => <div key={k} className="px-2 sm:px-4 py-4 text-center font-mono-num text-amber-300 font-bold">{fmt(sym, r[k])}</div>)}
          </div>
        ))}
        {rows.length === 0 && <div className="px-4 py-8 text-center text-slate-500 text-sm">Pricing coming soon — WhatsApp us for a quote.</div>}
      </div>
      <p className="text-xs text-slate-500 mt-3">{site.pricing_note}</p>
    </Section>
  );
}

function Gallery({ photos }) {
  return (
    <Section id="gallery" kicker="Our Work" title="Recent jobs from the workshop">
      {photos.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 p-14 text-center text-slate-500" data-testid="site-gallery-empty"><Images className="w-8 h-8 mx-auto mb-3 text-slate-600" />Photos coming soon.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4" data-testid="site-gallery">
          {photos.map((p, i) => (
            <figure key={p.id} className={`group relative overflow-hidden rounded-2xl bg-slate-900 ${i % 5 === 0 ? "md:col-span-2" : ""}`}>
              <img src={`${BACKEND_URL}${p.url}`} alt={p.caption || "Workshop job"} loading="lazy" className={`w-full h-full object-cover ${i % 5 === 0 ? "aspect-video" : "aspect-[4/3]"} group-hover:scale-105 transition-transform duration-500`} />
              {p.caption && <figcaption className="absolute inset-x-0 bottom-0 p-3 text-xs sm:text-sm font-semibold bg-gradient-to-t from-black/80 to-transparent">{p.caption}</figcaption>}
            </figure>
          ))}
        </div>
      )}
    </Section>
  );
}

const StarRow = ({ n, size = "w-4 h-4" }) => (
  <div className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${size} ${i <= Math.round(n) ? "fill-amber-400 text-amber-400" : "text-slate-700"}`} />)}
  </div>
);

function ReviewSlider({ items }) {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const scrollTo = (i) => {
    const el = ref.current;
    if (!el) return;
    const n = (i + items.length) % items.length;
    el.children[n]?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
    setIdx(n);
  };
  useEffect(() => {
    if (items.length < 2) return undefined;
    const t = setInterval(() => scrollTo(idx + 1), 6000);
    return () => clearInterval(t);
  }, [idx, items.length]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="relative" data-testid="site-reviews-list">
      <div ref={ref} className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        onScroll={(e) => { const el = e.currentTarget; const w = el.children[0]?.offsetWidth || 1; setIdx(Math.round(el.scrollLeft / (w + 16))); }}>
        {items.map((it, i) => (
          <figure key={it._id || `${it.name}-${i}`} className="snap-start shrink-0 w-[85%] sm:w-[calc(50%-8px)] rounded-2xl border border-slate-800 bg-[#151D2E] p-5 flex flex-col" data-testid={`site-review-card-${i}`}>
            <StarRow n={it.stars || 5} />
            <blockquote className="text-sm text-slate-300 mt-3 flex-1 leading-relaxed whitespace-pre-line">“{it.text}”</blockquote>
            <figcaption className="mt-4 flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-amber-400/15 text-amber-300 grid place-items-center font-black text-sm">{(it.name || "?").slice(0, 1).toUpperCase()}</span>
              <div><div className="font-semibold text-sm">{it.name}</div>{it.date && <div className="text-[11px] text-slate-500">{it.date}</div>}</div>
            </figcaption>
          </figure>
        ))}
      </div>
      {items.length > 1 && (
        <div className="flex items-center justify-between mt-3">
          <div className="flex gap-1.5">{items.map((it, i) => <button key={`${it.name || ""}-${it.date || i}`} onClick={() => scrollTo(i)} aria-label={`Review ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-amber-400" : "w-1.5 bg-slate-700"}`} />)}</div>
          <div className="flex gap-2">
            <button onClick={() => scrollTo(idx - 1)} data-testid="site-reviews-prev" className="w-9 h-9 rounded-full border border-slate-700 grid place-items-center text-slate-300 hover:border-amber-400 hover:text-amber-300"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => scrollTo(idx + 1)} data-testid="site-reviews-next" className="w-9 h-9 rounded-full border border-slate-700 grid place-items-center text-slate-300 hover:border-amber-400 hover:text-amber-300"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

function Reviews({ reviews }) {
  const r = reviews || {};
  const items = (r.items || []).filter((x) => x.text || x.name);
  const empty = !items.length && !(r.rating > 0);
  return (
    <Section id="reviews" kicker="Customer Reviews" title="What drivers say about us">
      <div className="grid lg:grid-cols-[280px_1fr] gap-6 items-start">
        <div className="rounded-2xl border border-slate-800 bg-[#151D2E] p-6 text-center" data-testid="site-reviews-summary">
          <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-widest text-slate-400 font-semibold"><span className="inline-block w-5 h-5 rounded-full bg-white text-[#4285F4] font-black text-[13px] leading-5">G</span> Google Rating</div>
          <div className="font-heading text-6xl font-black text-amber-400 mt-3 leading-none" data-testid="site-reviews-rating">{r.rating > 0 ? Number(r.rating).toFixed(1) : "4.8"}</div>
          <div className="flex justify-center mt-3"><StarRow n={r.rating || 4.8} size="w-5 h-5" /></div>
          <div className="text-sm text-slate-400 mt-2">{r.count > 0 ? `Based on ${r.count} Google reviews` : "Rated by our customers on Google"}</div>
          {r.google_url && (
            <Button asChild className="mt-5 w-full bg-white hover:bg-slate-100 text-slate-900 font-bold"><a href={safeUrl(r.google_url)} target="_blank" rel="noreferrer" data-testid="site-reviews-google-link">Read all reviews on Google <ExternalLink className="w-4 h-4 ml-2" /></a></Button>
          )}
        </div>
        <div className="min-w-0">
          {empty ? (
            <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 p-10 text-center text-slate-500 text-sm" data-testid="site-reviews-empty">
              <Star className="w-8 h-8 mx-auto mb-3 text-slate-600" />
              {r.google_url ? <>See what our customers say — <a href={safeUrl(r.google_url)} target="_blank" rel="noreferrer" className="text-amber-300 hover:text-amber-200 underline underline-offset-4" data-testid="site-reviews-empty-link">read our reviews on Google</a>.</> : "Customer reviews coming soon — ask us about our recent work."}
            </div>
          ) : (
            <ReviewSlider items={items} />
          )}
        </div>
      </div>
    </Section>
  );
}

function Contact({ shop, site }) {
  const wa = shop?.phone ? `https://wa.me/${normalizePhone(shop.phone, shop.country_code)}` : null;
  const Item = ({ icon: Icon, label, value, href, testid }) => value ? (
    <a href={href || undefined} className="flex items-start gap-3 rounded-2xl border border-slate-800 bg-[#151D2E] p-5 hover:border-amber-500/40 transition-colors" data-testid={testid}>
      <Icon className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" /><div><div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div><div className="font-semibold mt-0.5 whitespace-pre-line">{value}</div></div>
    </a>
  ) : null;
  return (
    <Section id="contact" kicker="Contact Us" title="Drop by or message us for a quote">
      <div className="grid sm:grid-cols-2 gap-4">
        <Item icon={MapPin} label="Workshop" value={shop?.address} href={shop?.address ? `https://maps.google.com/?q=${encodeURIComponent(shop.address)}` : null} testid="site-contact-address" />
        <Item icon={Phone} label="Phone / WhatsApp" value={shop?.phone} href={wa} testid="site-contact-phone" />
        <Item icon={Mail} label="Email" value={shop?.email} href={shop?.email ? `mailto:${shop.email}` : null} testid="site-contact-email" />
        <Item icon={Clock} label="Opening hours" value={site.opening_hours} testid="site-contact-hours" />
      </div>
      {!shop?.address && !shop?.phone && <p className="text-xs text-slate-500 mt-4">Owner: add your address, phone and email under Prices → General to show them here.</p>}
    </Section>
  );
}

export default function PublicSite() {
  const [data, setData] = useState(null);
  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    axios.get(`${API}/public/site`).then((r) => setData(r.data)).catch(() => setData({ shop: {}, site: {} }));
    axios.get(`${API}/gallery`).then((r) => setPhotos(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="min-h-screen workshop-bg" />;
  const { shop, site } = data;
  const sym = shop?.currency_symbol || "$";
  return (
    <div className="min-h-screen workshop-bg text-slate-100" data-testid="public-site">
      <TopBar shop={shop} />
      <Hero site={site} shop={shop} />
      <Section id="about" kicker="About Us" title="A workshop built on craft and trust">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 items-start">
          <p className="text-base md:text-lg text-slate-300 leading-relaxed whitespace-pre-line" data-testid="site-about">{site.about}</p>
          <div className="grid grid-cols-3 gap-3">
            {[["35+", "Years"], ["2K", "Clear coats"], ["100%", "Colour match"]].map(([n, l]) => (
              <div key={l} className="rounded-2xl border border-slate-800 bg-[#151D2E] p-4 text-center"><div className="font-heading text-2xl font-black text-amber-400">{n}</div><div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">{l}</div></div>
            ))}
          </div>
        </div>
      </Section>
      <Section id="services" kicker="Products & Services" title="Everything your paintwork needs">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="site-services">
          {(site.services || []).map((s, i) => { const Icon = ICONS[i % ICONS.length]; return (
            <div key={s._id || `${s.title}-${i}`} className="rounded-2xl border border-slate-800 bg-[#151D2E] p-6 hover:border-sky-500/40 hover:-translate-y-0.5 transition-all">
              <div className="w-10 h-10 rounded-xl bg-sky-500/15 grid place-items-center"><Icon className="w-5 h-5 text-sky-300" /></div>
              <h3 className="font-heading font-bold text-lg mt-4">{s.title}</h3>
              <p className="text-sm text-slate-400 mt-1.5">{s.desc}</p>
            </div>
          ); })}
        </div>
      </Section>
      <Pricing site={site} sym={sym} />
      <Gallery photos={photos} />
      <Reviews reviews={site.reviews} />
      <Contact shop={shop} site={site} />
      <footer className="border-t border-slate-800 py-8 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} {shop?.name || BRAND.fullName} · <Link to="/login" className="hover:text-amber-300" data-testid="site-footer-login">Staff login</Link>
      </footer>
    </div>
  );
}
